import { useState, useEffect, useRef } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const OUTCOME_METRICS = [
    { target: 60, suffix: '%', label: 'Projected Cost Savings', prefix: 'Up to', delay: 0 },
    { target: 3, suffix: 'x', label: 'More Throughput', prefix: 'Up to', delay: 200 },
    { target: 0, suffix: 's', label: 'Cold Starts', prefix: '', delay: 400 },
];

const TRUST_SIGNALS = [
    { value: '5 min', label: 'to deploy', detail: 'One helm install' },
    { value: '100%', label: 'self-hosted', detail: 'Your cluster, your data' },
    { value: '0', label: 'external dependencies', detail: 'Self-contained' },
];

export const OutcomeMetricsBar = () => {
    const { ref, isVisible } = useScrollAnimation({ threshold: 0.2, triggerOnce: true });
    const [counts, setCounts] = useState([0, 0, 0]);
    const startTimeRef = useRef<number | null>(null);
    const rafRef = useRef<number>(0);
    const hasAnimated = useRef(false);

    useEffect(() => {
        if (!isVisible || hasAnimated.current) return;
        hasAnimated.current = true;

        const duration = 2000;

        const animate = (timestamp: number) => {
            if (!startTimeRef.current) startTimeRef.current = timestamp;
            const elapsed = timestamp - startTimeRef.current;

            const newCounts = OUTCOME_METRICS.map((m) => {
                const adjustedElapsed = Math.max(0, elapsed - m.delay);
                const progress = Math.min(adjustedElapsed / duration, 1);
                const eased = 1 - Math.pow(1 - progress, 3);
                return Math.round(m.target * eased);
            });

            setCounts(newCounts);

            if (elapsed < duration + 400) {
                rafRef.current = requestAnimationFrame(animate);
            }
        };

        rafRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(rafRef.current);
    }, [isVisible]);

    return (
        <section ref={ref} className="py-16 bg-obsidian-900">
            <div className="max-w-5xl mx-auto px-6">
                {/* Outcome metrics */}
                <div className="grid grid-cols-3 gap-4 md:gap-6 max-w-2xl mx-auto mb-12">
                    {OUTCOME_METRICS.map((m, i) => (
                        <div
                            key={i}
                            className="text-center p-6 rounded-2xl border border-volt-500/20 bg-white/[0.02] shadow-[0_0_20px_rgba(163,230,53,0.05)]"
                        >
                            {m.prefix && <div className="text-xs text-gray-500 mb-1">{m.prefix}</div>}
                            <div className="text-4xl md:text-5xl font-bold text-volt-500 mb-2">
                                {counts[i]}{m.suffix}
                            </div>
                            <div className="text-sm text-gray-400">{m.label}</div>
                        </div>
                    ))}
                </div>

                {/* Trust signals */}
                <div className="flex flex-wrap justify-center gap-8 md:gap-12 mb-10">
                    {TRUST_SIGNALS.map((signal, i) => (
                        <div key={i} className="text-center">
                            <div className="text-2xl md:text-3xl font-bold text-white">{signal.value}</div>
                            <div className="text-xs text-gray-400">{signal.label}</div>
                            <div className="text-xs text-gray-600">{signal.detail}</div>
                        </div>
                    ))}
                </div>

                {/* Positioning tagline */}
                <p className="text-center text-lg text-gray-500 italic">
                    Everyone else reinvented the reactive OS. <span className="text-volt-400 not-italic font-semibold">We built the predictive one.</span>
                </p>
            </div>
        </section>
    );
};
